/**
 * 工作流服务模块：获取公开工作流列表和详情 API
 */

import { apiService } from './api'
import type {
  FormComponent,
  WorkflowListParams,
  WorkflowListData,
  WorkflowApiResponse,
  WorkflowDetail,
} from '@/types/workflow'

const WORKFLOW_ENDPOINT = '/api/workflows' as const

/**
 * 生成稳定的表单组件标识，兜底后端返回空字符串 ID 的情况。
 * @param component 原始表单组件
 * @param index 当前组件下标
 * @returns 可用于前端渲染的稳定唯一标识
 */
function normalizeFormComponentID(
  component: FormComponent,
  index: number
): string {
  const componentID = component.id.trim()

  if (componentID !== '') {
    return componentID
  }

  const paramName = component.param_name.trim()
  if (paramName !== '') {
    return `component-${paramName}`
  }

  const nodeID = component.node_id?.trim()
  const nodeField = component.node_field?.trim()
  if (nodeID !== undefined && nodeID !== '' && nodeField !== undefined && nodeField !== '') {
    return `component-${nodeID}-${nodeField}-${String(index)}`
  }

  return `component-${component.component_type}-${String(component.sort_order)}-${String(index)}`
}

/**
 * 标准化表单组件配置，兼容后端缺失数组字段的情况
 * @param components 原始表单组件列表
 * @returns 标准化后的表单组件列表
 */
function normalizeFormComponents(
  components: WorkflowDetail['form_components'] | null | undefined
): FormComponent[] {
  if (!Array.isArray(components)) {
    return []
  }

  return components.map((component, index) => ({
    ...component,
    id: normalizeFormComponentID(component, index),
    options: Array.isArray(component.options) ? component.options : null,
  }))
}

/**
 * 标准化工作流详情数据，避免页面直接消费脏数据
 * @param workflow 原始工作流详情
 * @returns 标准化后的工作流详情
 */
function normalizeWorkflowDetail(workflow: WorkflowDetail): WorkflowDetail {
  return {
    ...workflow,
    form_components: normalizeFormComponents(workflow.form_components),
    output_nodes: Array.isArray(workflow.output_nodes) ? workflow.output_nodes : [],
  }
}

/**
 * 获取公开工作流列表
 * @param params 查询参数
 * @returns 工作流列表数据
 */
export async function getPublicWorkflowList(
  params?: WorkflowListParams
): Promise<WorkflowListData> {
  const queryParams: Record<string, string> = {}

  if (params?.page !== undefined) {
    queryParams['page'] = String(params.page)
  }
  if (params?.pageSize !== undefined) {
    queryParams['pageSize'] = String(params.pageSize)
  }

  const response = await apiService.get<WorkflowApiResponse<WorkflowListData>>(
    WORKFLOW_ENDPOINT,
    queryParams
  )
  return response.data
}

/**
 * 获取工作流详情
 * @param id 工作流 ID
 * @returns 工作流详情数据（包含 form_components）
 */
export async function getWorkflowDetail(id: string): Promise<WorkflowDetail> {
  const response = await apiService.get<WorkflowApiResponse<WorkflowDetail>>(
    `${WORKFLOW_ENDPOINT}/${id}`
  )
  return normalizeWorkflowDetail(response.data)
}
